/*
 * MnZ Foto-Editor + Farbvorschau (SCHNAUD, Malen nach Zahlen nach eigenem Foto)
 * Gehört zu snippets/mnz-editor.liquid + snippets/mnz-color-preview.liquid, wird von assets/mnz-buybox.js genutzt.
 * Muss vor mnz-buybox.js geladen werden (beide defer, Reihenfolge in sections/main-product-mnz.liquid).
 *
 * Aufbau:
 *  - MnzEditorCore: reine Rechnung ohne DOM (Ausschnitt, Drehung, Zoom, Cloudinary-URL, Auflösungswarnung).
 *  - mnzQuantFactory: Farbreduktion (k-means++ in Lab, fester Seed, Modus-Filter). In sich geschlossen, läuft im
 *    Web Worker (Blob-URL, weil die Assets cross-origin auf cdn.shopify.com liegen) oder in Häppchen im Haupt-Thread.
 *  - MnzEditor: Editor im Buybox-Fluss (Rahmen, Werkzeugleiste, Finger/Maus/Tastatur, Lupe, Farbvorschau-Popup).
 *
 * Koordinaten: Der Ausschnitt gilt im Bild NACH der Drehung (im Uhrzeigersinn) und in Pixeln des hochgeladenen
 * Originals (Breite/Höhe aus der Cloudinary-Antwort). Die URL dreht deshalb zuerst (a_) und schneidet dann zu (c_crop).
 * Alles rechnet im Browser, pro Bedienschritt entsteht keine Cloudinary-Transformation.
 */
(function (root) {
  'use strict';

  /* =====================================================================
   * Rechnung (pure)
   * ===================================================================== */
  var E = {};

  E.MAX_ZOOM = 4; // höchstens 4x über "cover"
  E.ZOOM_STEP = 1.1; // + und − je ca. 10 %
  E.PAN_STEP = 0.05; // Pfeile: 5 % der Rahmenbreite
  E.WORK_MAX = 2000; // Arbeitskopie im Browser: lange Kante
  E.DEFAULT_ASPECT = 0.8; // 4:5, falls die Größe nicht lesbar ist
  E.LOWRES_SAY_DELAY = 600; // ms: Ansage des Auflösungshinweises erst, wenn die Stufe so lange steht
  E.LOWRES_LIVE_KEEP = 20000; // ms: danach Live-Region leeren (keine veraltete Kopie im Lesemodus)

  function clamp(v, lo, hi) {
    if (lo > hi) return (lo + hi) / 2;
    return v < lo ? lo : (v > hi ? hi : v);
  }
  E.clamp = clamp;

  // "40x50 cm" -> {short: 40, long: 50}; null, wenn keine Maße im Text stehen
  E.parseSize = function (value) {
    var m = String(value || '').match(/(\d+(?:[.,]\d+)?)\s*[x×X*]\s*(\d+(?:[.,]\d+)?)/);
    if (!m) return null;
    var a = parseFloat(m[1].replace(',', '.'));
    var b = parseFloat(m[2].replace(',', '.'));
    if (!(a > 0 && b > 0)) return null;
    return { short: Math.min(a, b), long: Math.max(a, b) };
  };

  // Seitenverhältnis Breite/Höhe des Rahmens aus Größe und Format (Hochformat 4:5, Querformat 5:4)
  E.frameAspect = function (sizeValue, format) {
    var s = E.parseSize(sizeValue);
    var r = s ? s.short / s.long : E.DEFAULT_ASPECT;
    return format === 'landscape' ? 1 / r : r;
  };

  E.normRot = function (rot) {
    var r = Math.round((+rot || 0) / 90) * 90;
    return ((r % 360) + 360) % 360;
  };

  // Maße des Bildes nach der Drehung
  E.rotatedDims = function (w, h, rot) {
    return E.normRot(rot) % 180 === 0 ? { w: w, h: h } : { w: h, h: w };
  };

  // Größter Ausschnitt mit Seitenverhältnis aspect, der ganz ins Bild passt (= Zoom 1, "cover")
  E.coverSize = function (rw, rh, aspect) {
    if (rw / rh > aspect) return { w: rh * aspect, h: rh };
    return { w: rw, h: rw / aspect };
  };

  // Ansicht: {rot, zoom, cx, cy}; cx/cy = Mitte des Ausschnitts, relativ (0..1) im gedrehten Bild
  E.defaultView = function () {
    return { rot: 0, zoom: 1, cx: 0.5, cy: 0.5 };
  };

  // Ansicht in gültige Grenzen bringen: Zoom 1..MAX, Ausschnitt nie über den Bildrand (Bild füllt den Rahmen immer)
  E.normView = function (view, W, H, aspect) {
    var rot = E.normRot(view.rot);
    var d = E.rotatedDims(W, H, rot);
    var zoom = clamp(isFinite(view.zoom) ? +view.zoom : 1, 1, E.MAX_ZOOM);
    var c = E.coverSize(d.w, d.h, aspect);
    var hw = c.w / zoom / 2 / d.w;
    var hh = c.h / zoom / 2 / d.h;
    return {
      rot: rot,
      zoom: zoom,
      cx: clamp(isFinite(view.cx) ? +view.cx : 0.5, hw, 1 - hw),
      cy: clamp(isFinite(view.cy) ? +view.cy : 0.5, hh, 1 - hh)
    };
  };

  // Ausschnitt in Pixeln des gedrehten Originals (Gleitkomma). rw/rh = Maße des gedrehten Originals.
  E.cropRect = function (W, H, view, aspect) {
    var v = E.normView(view, W, H, aspect);
    var d = E.rotatedDims(W, H, v.rot);
    var c = E.coverSize(d.w, d.h, aspect);
    var w = c.w / v.zoom;
    var h = c.h / v.zoom;
    return { x: v.cx * d.w - w / 2, y: v.cy * d.h - h / 2, w: w, h: h, rot: v.rot, rw: d.w, rh: d.h };
  };

  // Ganzzahliger Ausschnitt für URL und Bestellung: gerundet, nie über den Rand
  E.cropInt = function (r) {
    var w = Math.max(1, Math.min(r.rw, Math.round(r.w)));
    var h = Math.max(1, Math.min(r.rh, Math.round(r.h)));
    var x = Math.min(Math.max(0, Math.round(r.x)), r.rw - w);
    var y = Math.min(Math.max(0, Math.round(r.y)), r.rh - h);
    return { x: x, y: y, w: w, h: h, rot: r.rot };
  };

  // 90° im Uhrzeigersinn: Bildpunkt (u, v) landet bei (1 − v, u). Zoom bleibt, Grenzen danach neu.
  E.rotateView = function (view) {
    return { rot: E.normRot(view.rot + 90), zoom: view.zoom, cx: 1 - view.cy, cy: view.cx };
  };

  // Verschieben: dx/dy in Rahmenbreiten, positiv = Foto wandert nach rechts/unten (wie beim Ziehen mit dem Finger)
  E.panView = function (view, dx, dy, W, H, aspect) {
    var v = E.normView(view, W, H, aspect);
    var r = E.cropRect(W, H, v, aspect);
    return E.normView({ rot: v.rot, zoom: v.zoom, cx: v.cx - (dx * r.w) / r.rw, cy: v.cy - (dy * r.w) / r.rh }, W, H, aspect);
  };

  // Zoomen um einen Ankerpunkt (fx/fy relativ im Rahmen, 0.5/0.5 = Mitte); der Bildpunkt unter dem Anker bleibt stehen
  E.zoomView = function (view, factor, fx, fy, W, H, aspect) {
    var v = E.normView(view, W, H, aspect);
    var r = E.cropRect(W, H, v, aspect);
    var z = clamp(v.zoom * factor, 1, E.MAX_ZOOM);
    var ax = r.x + fx * r.w;
    var ay = r.y + fy * r.h;
    var nw = (r.w * v.zoom) / z;
    var nh = (r.h * v.zoom) / z;
    return E.normView({ rot: v.rot, zoom: z, cx: (ax - fx * nw + nw / 2) / r.rw, cy: (ay - fy * nh + nh / 2) / r.rh }, W, H, aspect);
  };

  // Welche Richtungen gehen noch (für aria-disabled der Buttons)
  E.limits = function (view, W, H, aspect) {
    var v = E.normView(view, W, H, aspect);
    var r = E.cropRect(W, H, v, aspect);
    var eps = 0.5; // halbes Pixel im Original
    return {
      zoomIn: v.zoom < E.MAX_ZOOM - 1e-9,
      zoomOut: v.zoom > 1 + 1e-9,
      left: r.x + r.w < r.rw - eps, // Foto nach links schieben = Ausschnitt nach rechts
      right: r.x > eps,
      up: r.y + r.h < r.rh - eps,
      down: r.y > eps
    };
  };

  // Cloudinary-Auslieferung genau dieses Ausschnitts (inkl. Drehung) aus der secure_url des Uploads.
  // Reihenfolge: a_<grad> (dreht im Uhrzeigersinn, nach der automatischen EXIF-Drehung) -> c_crop im gedrehten Bild.
  // Die Version (/v123/) bleibt drin: der Warenkorb setzt seine Vorschau-Größe direkt davor.
  E.cropUrl = function (secureUrl, c) {
    var url = String(secureUrl || '');
    var marker = '/image/upload/';
    var i = url.indexOf(marker);
    if (i < 0 || !c) return url;
    var head = url.slice(0, i + marker.length);
    var tail = url.slice(i + marker.length).replace(/\.[a-z0-9]+$/i, '') + '.jpg';
    var t = [];
    if (c.rot) t.push('a_' + c.rot);
    t.push('c_crop,x_' + c.x + ',y_' + c.y + ',w_' + c.w + ',h_' + c.h);
    t.push('f_jpg,q_95');
    return head + t.join('/') + '/' + tail;
  };

  // Arbeitskopie für Formate, die der Browser nicht selbst lesen kann (HEIC): einmal pro Upload als JPEG, lange Kante max. 2000 px
  E.workUrl = function (secureUrl) {
    var url = String(secureUrl || '');
    var marker = '/image/upload/';
    var i = url.indexOf(marker);
    if (i < 0) return url;
    return url.slice(0, i + marker.length) + 'c_limit,w_' + E.WORK_MAX + ',h_' + E.WORK_MAX + ',f_jpg,q_85/' +
      url.slice(i + marker.length).replace(/\.[a-z0-9]+$/i, '') + '.jpg';
  };

  // Rohdaten für die Bestellung (versteckte Eigenschaft), damit der Ausschnitt jederzeit neu erzeugt werden kann
  E.cropData = function (c, photo) {
    return {
      public_id: photo.publicId || '',
      original_width: photo.width,
      original_height: photo.height,
      rotation_cw: c.rot,
      x: c.x,
      y: c.y,
      w: c.w,
      h: c.h
    };
  };

  // Mindestgröße der kürzeren Kante (Originalpixel) für eine Größe: aus den Settings, sonst ca. 22,5 px je cm
  E.minPixels = function (sizeValue, thresholds) {
    var t = thresholds && thresholds[sizeValue];
    if (t > 0) return t;
    var s = E.parseSize(sizeValue);
    var perCm = (thresholds && thresholds.perCm) || 22.5;
    return s ? Math.round(s.short * perCm) : 0;
  };

  E.isLowRes = function (c, sizeValue, thresholds) {
    var min = E.minPixels(sizeValue, thresholds);
    return !!c && min > 0 && Math.min(c.w, c.h) < min;
  };

  // Stufe des Auflösungshinweises: 0 = keiner, 1 = dezenter Hinweis (unter der Mindestgröße der gewählten Größe),
  // 2 = deutlicher Warnkasten (kürzere Kante unter strongMin, für alle Größen; 0 = Stufe 2 aus). Nie eine Sperre.
  E.lowResLevel = function (c, sizeValue, thresholds, strongMin) {
    if (!c) return 0;
    if (strongMin > 0 && Math.min(c.w, c.h) < strongMin) return 2;
    return E.isLowRes(c, sizeValue, thresholds) ? 1 : 0;
  };

  // Ganze Zahl mit deutschem Tausenderpunkt: 1500 -> "1.500" (ohne Intl, überall gleich)
  E.formatInt = function (n) {
    var r = Math.round(+n || 0);
    var s = String(Math.abs(r)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return r < 0 ? '-' + s : s;
  };

  // Text mit {platzhaltern} in Stücke zerlegen: [{text}, {key}, ...] (für den DOM-Aufbau ohne innerHTML).
  // {höhe} gilt wie {hoehe}; unbekannte Platzhalter bleiben als Text stehen.
  E.PLACEHOLDERS = { breite: 'breite', hoehe: 'hoehe', 'höhe': 'hoehe' };
  E.splitTemplate = function (template) {
    var out = [];
    var re = /\{([a-zäöüß]+)\}/gi;
    var s = String(template || '');
    var last = 0;
    var m;
    while ((m = re.exec(s))) {
      var key = E.PLACEHOLDERS[m[1].toLowerCase()];
      if (!key) continue;
      if (m.index > last) out.push({ text: s.slice(last, m.index) });
      out.push({ key: key });
      last = m.index + m[0].length;
    }
    if (last < s.length) out.push({ text: s.slice(last) });
    return out;
  };

  // Fertiger Text des Warnkastens: Maße des Ausschnitts in Originalpixeln, gerundet, mit Tausenderpunkt
  E.fillDims = function (template, c) {
    var map = { breite: E.formatInt(c ? c.w : 0), hoehe: E.formatInt(c ? c.h : 0) };
    return E.splitTemplate(template).map(function (p) { return p.key ? map[p.key] : p.text; }).join('');
  };

  // Arbeitskopie: Zielmaße (lange Kante max. WORK_MAX)
  E.workSize = function (w, h, max) {
    var m = max || E.WORK_MAX;
    var s = Math.min(1, m / Math.max(w, h));
    return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) };
  };

  // Passt die Arbeitskopie zu den Cloudinary-Maßen? 'same' | 'swapped' | null (dann Cloudinary-JPEG nehmen)
  E.matchOrientation = function (workW, workH, W, H) {
    var tol = 0.02;
    var a = workW / workH;
    if (Math.abs(a / (W / H) - 1) < tol) return 'same';
    if (Math.abs(a / (H / W) - 1) < tol) return 'swapped';
    return null;
  };

  // Farbanzahl aus dem Optionswert: "36 Farben" -> 36
  E.colorCount = function (value) {
    var m = String(value || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  };

  E.fill = function (template, map) {
    return String(template || '').replace(/\[(\w+)\]/g, function (all, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? String(map[k]) : all;
    });
  };

  /* =====================================================================
   * Farbreduktion. In sich geschlossen (keine Variablen von außen), weil der Quelltext 1:1 in den Worker wandert.
   * Ablauf: 6-Bit-Histogramm -> Lab je Farbe -> k-means++ (gewichtet, Seed fest) -> Lloyd bis stabil ->
   *         jedes Pixel auf das nächste Zentrum -> Modus-Filter gegen Pixelrauschen -> RGBA.
   * createJob(...).step(deadline) arbeitet in Häppchen und gibt true zurück, wenn fertig (für den Haupt-Thread).
   * ===================================================================== */
  function mnzQuantFactory() {
    var BITS = 6;
    var SHIFT = 8 - BITS;
    var SIZE = 1 << (BITS * 3);
    var LIN = new Float64Array(256);
    for (var li = 0; li < 256; li++) {
      var cc = li / 255;
      LIN[li] = cc <= 0.04045 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
    }

    function f(t) {
      return t > 0.008856451679 ? Math.cbrt(t) : 7.787037037 * t + 16 / 116;
    }

    function toLab(r, g, b, out, o) {
      var R = LIN[r];
      var G = LIN[g];
      var B = LIN[b];
      var fx = f((0.4124564 * R + 0.3575761 * G + 0.1804375 * B) / 0.95047);
      var fy = f(0.2126729 * R + 0.7151522 * G + 0.072175 * B);
      var fz = f((0.0193339 * R + 0.119192 * G + 0.9503041 * B) / 1.08883);
      out[o] = 116 * fy - 16;
      out[o + 1] = 500 * (fx - fy);
      out[o + 2] = 200 * (fy - fz);
    }

    // Kleiner deterministischer Zufall (mulberry32)
    function rng(seed) {
      var a = seed >>> 0;
      return function () {
        a = (a + 0x6d2b79f5) >>> 0;
        var t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }

    /**
     * input: {data: Uint8ClampedArray (RGBA), width, height, n, seed?, maxIter?, mode? (Fenster 3/5/0)}
     * Ergebnis (job.result): {data: Uint8ClampedArray RGBA, palette: [[r,g,b],...], colors: Anzahl Zentren, iterations}
     */
    function createJob(input) {
      var W = input.width;
      var H = input.height;
      var px = input.data;
      var N = Math.max(1, Math.min(255, input.n | 0));
      var maxIter = input.maxIter || 24;
      var modeSize = input.mode === 0 ? 0 : (input.mode || 5);
      var random = rng(input.seed == null ? 20260927 : input.seed);
      var total = W * H;

      var counts = null;
      var U = 0;
      var uKey = null;
      var uW = null;
      var uLab = null;
      var uRGB = null;
      var K = 0;
      var cLab = null;
      var dist = null;
      var assign = null;
      var lut = null;
      var labels = null;
      var out = null;
      var palette = null;

      var phase = 'hist';
      var pos = 0;
      var iter = 0;
      var sums = null;
      var moved = 0;

      var job = { result: null, phase: function () { return phase; } };

      function nearest(o) {
        var L = uLab[o];
        var A = uLab[o + 1];
        var B = uLab[o + 2];
        var best = 0;
        var bd = Infinity;
        for (var k = 0, q = 0; k < K; k++, q += 3) {
          var d0 = L - cLab[q];
          var d1 = A - cLab[q + 1];
          var d2 = B - cLab[q + 2];
          var d = d0 * d0 + d1 * d1 + d2 * d2;
          if (d < bd) { bd = d; best = k; }
        }
        return best;
      }

      job.step = function (deadline) {
        var i;
        var n = 0;
        var CHECK = 4096;
        for (;;) {
          if (phase === 'hist') {
            if (!counts) counts = new Uint32Array(SIZE);
            for (; pos < total; pos++) {
              var p = pos * 4;
              counts[((px[p] >> SHIFT) << (BITS * 2)) | ((px[p + 1] >> SHIFT) << BITS) | (px[p + 2] >> SHIFT)]++;
              if (++n % (CHECK * 8) === 0 && deadline()) { pos++; return false; }
            }
            // Vorhandene Farben einsammeln (Mitte der 6-Bit-Zelle), Gewicht = Anzahl Pixel
            U = 0;
            for (i = 0; i < SIZE; i++) if (counts[i]) U++;
            uKey = new Uint32Array(U);
            uW = new Float64Array(U);
            uLab = new Float64Array(U * 3);
            uRGB = new Float64Array(U * 3);
            var j = 0;
            var half = (1 << SHIFT) >> 1;
            var mask = (1 << BITS) - 1;
            for (i = 0; i < SIZE; i++) {
              if (!counts[i]) continue;
              var r = (((i >> (BITS * 2)) & mask) << SHIFT) | half;
              var g = (((i >> BITS) & mask) << SHIFT) | half;
              var b = ((i & mask) << SHIFT) | half;
              uKey[j] = i;
              uW[j] = counts[i];
              uRGB[j * 3] = r;
              uRGB[j * 3 + 1] = g;
              uRGB[j * 3 + 2] = b;
              toLab(r, g, b, uLab, j * 3);
              j++;
            }
            counts = null;
            K = Math.min(N, U);
            cLab = new Float64Array(K * 3);
            dist = new Float64Array(U);
            assign = new Int32Array(U);
            phase = 'init';
            pos = 0;
          } else if (phase === 'init') {
            // k-means++: erstes Zentrum gewichtet zufällig, jedes weitere mit Wahrscheinlichkeit ~ Gewicht x Abstand²
            if (pos === 0) {
              var tw = 0;
              for (i = 0; i < U; i++) tw += uW[i];
              var t = random() * tw;
              var first = U - 1;
              for (i = 0; i < U; i++) { t -= uW[i]; if (t <= 0) { first = i; break; } }
              cLab[0] = uLab[first * 3];
              cLab[1] = uLab[first * 3 + 1];
              cLab[2] = uLab[first * 3 + 2];
              for (i = 0; i < U; i++) dist[i] = Infinity;
              pos = 1;
            }
            while (pos <= K) {
              var q = (pos - 1) * 3;
              var sum = 0;
              for (i = 0; i < U; i++) {
                var e0 = uLab[i * 3] - cLab[q];
                var e1 = uLab[i * 3 + 1] - cLab[q + 1];
                var e2 = uLab[i * 3 + 2] - cLab[q + 2];
                var dd = e0 * e0 + e1 * e1 + e2 * e2;
                if (dd < dist[i]) dist[i] = dd;
                sum += dist[i] * uW[i];
              }
              if (pos === K) { pos++; break; }
              var pick = -1;
              if (sum > 0) {
                var s = random() * sum;
                for (i = 0; i < U; i++) { s -= dist[i] * uW[i]; if (s <= 0 && dist[i] > 0) { pick = i; break; } }
              }
              if (pick < 0) {
                for (i = U - 1; i >= 0; i--) if (dist[i] > 0) { pick = i; break; }
              }
              if (pick < 0) pick = 0;
              cLab[pos * 3] = uLab[pick * 3];
              cLab[pos * 3 + 1] = uLab[pick * 3 + 1];
              cLab[pos * 3 + 2] = uLab[pick * 3 + 2];
              pos++;
              if (deadline()) return false;
            }
            phase = 'iter';
            pos = 0;
            iter = 0;
          } else if (phase === 'iter') {
            // Lloyd, gewichtet: zuordnen und Summen bilden
            if (pos === 0) { sums = new Float64Array(K * 4); moved = 0; }
            for (; pos < U; pos++) {
              var k2 = nearest(pos * 3);
              if (assign[pos] !== k2) moved++;
              assign[pos] = k2;
              var w2 = uW[pos];
              sums[k2 * 4] += uLab[pos * 3] * w2;
              sums[k2 * 4 + 1] += uLab[pos * 3 + 1] * w2;
              sums[k2 * 4 + 2] += uLab[pos * 3 + 2] * w2;
              sums[k2 * 4 + 3] += w2;
              if (++n % CHECK === 0 && deadline()) { pos++; return false; }
            }
            var shift = 0;
            var empty = [];
            for (var k = 0; k < K; k++) {
              var wk = sums[k * 4 + 3];
              if (wk > 0) {
                var nl = sums[k * 4] / wk;
                var na = sums[k * 4 + 1] / wk;
                var nb = sums[k * 4 + 2] / wk;
                shift = Math.max(shift, Math.abs(nl - cLab[k * 3]) + Math.abs(na - cLab[k * 3 + 1]) + Math.abs(nb - cLab[k * 3 + 2]));
                cLab[k * 3] = nl;
                cLab[k * 3 + 1] = na;
                cLab[k * 3 + 2] = nb;
              } else {
                empty.push(k);
              }
            }
            // Leere Zentren neu setzen: auf die Farbe mit dem größten gewichteten Abstand zu ihrem Zentrum
            for (var ei = 0; ei < empty.length; ei++) {
              var far = -1;
              var fd = -1;
              for (i = 0; i < U; i++) {
                var a3 = assign[i] * 3;
                var g0 = uLab[i * 3] - cLab[a3];
                var g1 = uLab[i * 3 + 1] - cLab[a3 + 1];
                var g2 = uLab[i * 3 + 2] - cLab[a3 + 2];
                var gd = (g0 * g0 + g1 * g1 + g2 * g2) * uW[i];
                if (gd > fd) { fd = gd; far = i; }
              }
              if (far >= 0) {
                cLab[empty[ei] * 3] = uLab[far * 3];
                cLab[empty[ei] * 3 + 1] = uLab[far * 3 + 1];
                cLab[empty[ei] * 3 + 2] = uLab[far * 3 + 2];
                assign[far] = empty[ei];
                shift = Infinity;
              }
            }
            iter++;
            pos = 0;
            if (iter >= maxIter || (shift < 0.25 && moved === 0)) {
              phase = 'palette';
            }
            if (deadline()) return false;
          } else if (phase === 'palette') {
            // Endgültige Zuordnung, Palette = gewichteter RGB-Mittelwert der Mitglieder (bleibt im Farbraum)
            var ps = new Float64Array(K * 4);
            lut = new Int16Array(SIZE);
            for (i = 0; i < U; i++) {
              var kk = nearest(i * 3);
              lut[uKey[i]] = kk;
              var ww = uW[i];
              ps[kk * 4] += uRGB[i * 3] * ww;
              ps[kk * 4 + 1] += uRGB[i * 3 + 1] * ww;
              ps[kk * 4 + 2] += uRGB[i * 3 + 2] * ww;
              ps[kk * 4 + 3] += ww;
            }
            palette = [];
            for (k = 0; k < K; k++) {
              var wp = ps[k * 4 + 3];
              if (wp > 0) {
                palette.push([Math.round(ps[k * 4] / wp), Math.round(ps[k * 4 + 1] / wp), Math.round(ps[k * 4 + 2] / wp)]);
              } else {
                // Zentrum ohne Mitglieder (nur bei extrem wenigen Farben): Lab -> sRGB
                palette.push(labToRgb(cLab[k * 3], cLab[k * 3 + 1], cLab[k * 3 + 2]));
              }
            }
            labels = new Uint8Array(total);
            phase = 'map';
            pos = 0;
          } else if (phase === 'map') {
            for (; pos < total; pos++) {
              var p2 = pos * 4;
              labels[pos] = lut[((px[p2] >> SHIFT) << (BITS * 2)) | ((px[p2 + 1] >> SHIFT) << BITS) | (px[p2 + 2] >> SHIFT)];
              if (++n % (CHECK * 8) === 0 && deadline()) { pos++; return false; }
            }
            lut = null;
            phase = modeSize ? 'mode' : 'render';
            pos = 0;
            if (modeSize) { job._src = labels; labels = new Uint8Array(total); job._cnt = new Uint16Array(K); }
          } else if (phase === 'mode') {
            // Modus-Filter: jedes Pixel bekommt die häufigste Farbe im Fenster (bei Gleichstand bleibt die eigene)
            var src = job._src;
            var cnt = job._cnt;
            var rad = modeSize >> 1;
            for (; pos < H; pos++) {
              var y0 = pos - rad < 0 ? 0 : pos - rad;
              var y1 = pos + rad >= H ? H - 1 : pos + rad;
              for (var x = 0; x < W; x++) {
                var x0 = x - rad < 0 ? 0 : x - rad;
                var x1 = x + rad >= W ? W - 1 : x + rad;
                var own = src[pos * W + x];
                var best = own;
                var bc = 0;
                var yy;
                var xx;
                for (yy = y0; yy <= y1; yy++) {
                  var row = yy * W;
                  for (xx = x0; xx <= x1; xx++) {
                    var lb = src[row + xx];
                    var cv = ++cnt[lb];
                    if (cv > bc || (cv === bc && lb === own)) { bc = cv; best = lb; }
                  }
                }
                if (cnt[own] === bc) best = own;
                labels[pos * W + x] = best;
                for (yy = y0; yy <= y1; yy++) {
                  var row2 = yy * W;
                  for (xx = x0; xx <= x1; xx++) cnt[src[row2 + xx]] = 0;
                }
              }
              if (deadline()) { pos++; return false; }
            }
            job._src = null;
            job._cnt = null;
            phase = 'render';
            pos = 0;
          } else if (phase === 'render') {
            if (!out) out = new Uint8ClampedArray(total * 4);
            for (; pos < total; pos++) {
              var c = palette[labels[pos]];
              var o = pos * 4;
              out[o] = c[0];
              out[o + 1] = c[1];
              out[o + 2] = c[2];
              out[o + 3] = 255;
              if (++n % (CHECK * 8) === 0 && deadline()) { pos++; return false; }
            }
            job.result = { data: out, width: W, height: H, palette: palette, colors: K, iterations: iter };
            phase = 'done';
            return true;
          } else {
            return true;
          }
        }
      };
      return job;
    }

    function labToRgb(L, a, b) {
      var fy = (L + 16) / 116;
      var fx = fy + a / 500;
      var fz = fy - b / 200;
      function inv(t) { var t3 = t * t * t; return t3 > 0.008856451679 ? t3 : (t - 16 / 116) / 7.787037037; }
      var X = inv(fx) * 0.95047;
      var Y = inv(fy);
      var Z = inv(fz) * 1.08883;
      var lin = [3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z, -0.969266 * X + 1.8760108 * Y + 0.041556 * Z, 0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z];
      return lin.map(function (v) {
        v = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(Math.max(0, v), 1 / 2.4) - 0.055;
        return Math.max(0, Math.min(255, Math.round(v * 255)));
      });
    }

    // Komplett in einem Rutsch (Worker, Node-Tests)
    function run(input) {
      var job = createJob(input);
      job.step(function () { return false; });
      return job.result;
    }

    return { createJob: createJob, run: run, labToRgb: labToRgb, toLab: toLab };
  }

  E.quantFactory = mnzQuantFactory;
  E.Quant = mnzQuantFactory();
  E.useWorker = true; // false = immer Haupt-Thread (Test-Harness)
  E.PREVIEW_LONG = 800; // Arbeitsauflösung der Farbvorschau (lange Kante)
  E.PREVIEW_SEED = 20260927;
  E.PREVIEW_MODE = 5; // Modus-Filter 5x5
  E.CACHE_MAX = 9;

  root.MnzEditorCore = E;

  /* =====================================================================
   * DOM
   * ===================================================================== */
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
  }

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function freeCanvas(c) {
    if (c && c.getContext) { c.width = 0; c.height = 0; } // Speicher auf Mobilgeräten sofort freigeben
  }

  // Bildausschnitt aus der (gedrehten) Arbeitskopie in eine Zielfläche zeichnen. Quellrechteck bleibt im Bild (Safari zeichnet sonst nichts).
  function drawCrop(ctx, src, r, dw, dh) {
    var sx = src.width / r.rw;
    var sy = src.height / r.rh;
    var x = Math.max(0, r.x * sx);
    var y = Math.max(0, r.y * sy);
    var w = Math.min(src.width - x, r.w * sx);
    var h = Math.min(src.height - y, r.h * sy);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, x, y, w, h, 0, 0, dw, dh);
  }

  /* ---------- Popup: gleiches Verhalten wie das Leinwand-Popup der Buybox ----------
   * <dialog> mit showModal() (Rest inert, ESC nativ), Klick auf den Hintergrund, Schließen-Buttons [data-mnz-dlg-close],
   * Scroll-Sperre über opts.lock, Fokus zurück auf den Öffner. Chrome feuert nach ESC nicht immer 'close',
   * daher zusätzlich das open-Attribut beobachten (_after läuft nur einmal je Öffnen).
   */
  function Dialog(el, opts) {
    var self = this;
    this.el = el;
    this.opts = opts || {};
    this.native = false;
    this.active = false;
    this.opener = null;
    el.addEventListener('pointerdown', function (e) { self.downOnBackdrop = e.target === el; });
    el.addEventListener('click', function (e) {
      var closer = e.target.closest ? e.target.closest('[data-mnz-dlg-close]') : null;
      if (closer) {
        e.preventDefault();
        self.close();
      } else if (e.target === el && self.downOnBackdrop !== false) {
        self.close();
      }
      self.downOnBackdrop = null;
    });
    // 'close' kommt asynchron: wurde inzwischen wieder geöffnet, gehört es zum alten Schließen und wird ignoriert
    el.addEventListener('close', function () { if (!el.hasAttribute('open')) self._after(); });
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (!el.hasAttribute('open')) self._after();
      }).observe(el, { attributes: true, attributeFilter: ['open'] });
    }
    // Ersatz ohne showModal: ESC und Fokus-Falle selbst
    el.addEventListener('keydown', function (e) {
      if (self.native) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        self.close();
      } else if (e.key === 'Tab') {
        var f = Array.prototype.filter.call(el.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'), function (n) { return !n.hidden && !n.disabled; });
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) {
          e.preventDefault();
          f[f.length - 1].focus();
        } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
          e.preventDefault();
          f[0].focus();
        }
      }
    });
  }

  Dialog.prototype.isOpen = function () {
    return this.el.hasAttribute('open');
  };

  Dialog.prototype.open = function (opener) {
    var d = this.el;
    if (d.hasAttribute('open')) return;
    this.opener = opener || null;
    if (this.opts.lock) this.opts.lock(true);
    this.active = true;
    this.native = false;
    if (typeof d.showModal === 'function') {
      try {
        d.showModal();
        this.native = true;
      } catch (err) {
        this.native = false;
      }
    }
    if (!this.native) {
      d.setAttribute('open', '');
      d.classList.add('mnz-dialog--fallback');
    }
    if (this.opts.onOpen) this.opts.onOpen();
    var target = typeof this.opts.focus === 'function' ? this.opts.focus() : d.querySelector('[data-mnz-dlg-close]');
    focusEl(target || d.querySelector('[data-mnz-dlg-close]'));
  };

  Dialog.prototype.close = function () {
    var d = this.el;
    if (!d.hasAttribute('open')) return;
    if (this.native && typeof d.close === 'function') {
      d.close();
    } else {
      d.removeAttribute('open');
      d.classList.remove('mnz-dialog--fallback');
    }
    this._after();
  };

  Dialog.prototype._after = function () {
    if (!this.active) return;
    this.active = false;
    if (this.opts.lock) this.opts.lock(false);
    if (this.opts.onClose) this.opts.onClose();
    if (this.opener && this.opener.isConnected && !this.opener.closest('[hidden]')) focusEl(this.opener);
  };

  E.Dialog = Dialog;

  /* ---------- Editor ----------
   * o: {host, strings, thresholds, strongMin, lock(bool), onChange(), onRotate(editor), colors() -> ['24 Farben', ...],
   *     selectedColor() -> '24 Farben', chooseColor(value)}
   * strongMin: kürzere Kante (Originalpixel), unter der statt des Hinweises der Warnkasten erscheint (strings.lowresStrong)
   */
  function Editor(rootEl, o) {
    this.root = rootEl;
    this.o = o || {};
    this.s = this.o.strings || {};
    this.host = this.o.host || document;
    this.frame = rootEl.querySelector('[data-mnz-ed-frame]');
    this.stage = rootEl.querySelector('.mnz-editor__stage');
    this.canvas = rootEl.querySelector('[data-mnz-ed-canvas]');
    this.tools = rootEl.querySelector('[data-mnz-ed-tools]');
    this.toggleBtn = rootEl.querySelector('[data-mnz-ed-toggle]');
    this.loupeBtn = rootEl.querySelector('[data-mnz-ed-loupe]');
    this.previewBtn = rootEl.querySelector('[data-mnz-preview-open]');
    this.lowresEl = rootEl.querySelector('[data-mnz-ed-lowres]');
    this.lowresTextEl = rootEl.querySelector('[data-mnz-ed-lowres-text]');
    this.lowresLiveEl = rootEl.querySelector('[data-mnz-ed-lowres-live]');
    this._lowres1 = this.lowresTextEl ? this.lowresTextEl.textContent : ''; // Stufe-1-Text, serverseitig gerendert
    this._lvl = 0; // angezeigte Stufe
    this._lvlSaid = 0; // zuletzt angesagte Stufe
    this.statusEl = rootEl.querySelector('[data-mnz-ed-status]');
    this.loadingEl = rootEl.querySelector('[data-mnz-ed-loading]');
    this.failedEl = rootEl.querySelector('[data-mnz-ed-failed]');

    this.view = E.defaultView();
    this.photo = null;
    this.W = 0;
    this.H = 0;
    this.aspect = E.DEFAULT_ASPECT;
    this.sizeValue = '';
    this.work = null;
    this.rotWork = null;
    this.rotWorkRot = -1;
    this.token = 0;
    this.cache = new Map();
    this.pointers = new Map();
    this._pending = {};
    this._qid = 0;
    this._queue = Promise.resolve();

    var loupeEl = this.host.querySelector('[data-mnz-loupe]');
    var previewEl = this.host.querySelector('[data-mnz-preview]');
    var self = this;
    var lock = this.o.lock;
    if (loupeEl) {
      this.loupeCanvas = loupeEl.querySelector('[data-mnz-loupe-canvas]');
      this.loupe = new Dialog(loupeEl, { lock: lock });
    }
    if (previewEl) {
      this.pv = {
        el: previewEl,
        tabs: previewEl.querySelector('[data-mnz-preview-tabs]'),
        panel: previewEl.querySelector('[data-mnz-preview-panel]'),
        stage: previewEl.querySelector('[data-mnz-preview-stage]'),
        canvas: previewEl.querySelector('[data-mnz-preview-canvas]'),
        busy: previewEl.querySelector('[data-mnz-preview-busy]'),
        failed: previewEl.querySelector('[data-mnz-preview-failed]'),
        choose: previewEl.querySelector('[data-mnz-preview-choose]'),
        colors: previewEl.querySelector('[data-mnz-preview-colors]'),
        value: null
      };
      this.preview = new Dialog(previewEl, {
        lock: lock,
        focus: function () { return self.pv.tabs.querySelector('[aria-selected="true"]'); },
        onClose: function () { self.pv.value = null; }
      });
      this._placeColors();
    }
    this._bind();
  }

  // R6: Farben-Akkordeon zweispaltig (Desktop, Handy quer) in der rechten Spalte, sonst als Leiste unten im Bildbereich,
  // damit das Bild auf dem Handy nicht kleiner wird
  Editor.prototype._placeColors = function () {
    var box = this.pv.colors;
    if (!box || !window.matchMedia) return;
    var fit = this.pv.el.querySelector('.mnz-preview__fit');
    var side = this.pv.panel.parentNode;
    var mq = window.matchMedia('(min-width: 990px) and (min-height: 501px), (orientation: landscape) and (max-height: 500px)');
    var first = true;
    var place = function () {
      var target = mq.matches ? side : fit;
      // rechte Spalte hat Platz: dort offen; als Leiste im Bild zu, damit das Bild frei bleibt (nur beim Wechsel, sonst gilt der Klick)
      if (first || box.parentNode !== target) box.open = mq.matches;
      if (box.parentNode !== target) target.appendChild(box);
      box.classList.toggle('mnz-preview__colors--overlay', !mq.matches);
      first = false;
    };
    place();
    if (mq.addEventListener) mq.addEventListener('change', place);
    else if (mq.addListener) mq.addListener(place);
  };

  Editor.prototype._bind = function () {
    var self = this;
    var frame = this.frame;

    this.tools.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-mnz-ed]') : null;
      if (!b || b.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      self.act(b.getAttribute('data-mnz-ed'));
    });

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var show = self.tools.hidden;
        self.tools.hidden = !show;
        self.toggleBtn.setAttribute('aria-expanded', show ? 'true' : 'false');
      });
    }

    if (this.loupeBtn && this.loupe) {
      this.loupeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.openLoupe(self.loupeBtn);
      });
    }

    if (this.previewBtn && this.preview) {
      this.previewBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.openPreview(self.previewBtn);
      });
      this.pv.tabs.addEventListener('click', function (e) {
        var t = e.target.closest ? e.target.closest('[role="tab"]') : null;
        if (t) self._selectTab(t.getAttribute('data-value'), false);
      });
      this.pv.tabs.addEventListener('keydown', function (e) {
        var list = Array.prototype.slice.call(self.pv.tabs.querySelectorAll('[role="tab"]'));
        var i = list.indexOf(document.activeElement);
        if (i < 0) return;
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = list[(i + 1) % list.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = list[(i - 1 + list.length) % list.length];
        else if (e.key === 'Home') next = list[0];
        else if (e.key === 'End') next = list[list.length - 1];
        if (!next) return;
        e.preventDefault();
        self._selectTab(next.getAttribute('data-value'), true);
      });
      this.pv.choose.addEventListener('click', function (e) {
        e.preventDefault();
        var value = self.pv.value;
        if (value && self.o.chooseColor) self.o.chooseColor(value);
        self.preview.close();
      });
    }

    // Finger/Maus: ein Zeiger verschiebt, zwei Zeiger zoomen (Pinch) um ihren Mittelpunkt und verschieben mit
    frame.addEventListener('pointerdown', function (e) {
      if (!self.photo || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (e.target.closest && e.target.closest('button')) return;
      try { frame.setPointerCapture(e.pointerId); } catch (err) { /* egal */ }
      self.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      self._gestureStart();
      if (e.pointerType === 'mouse') {
        e.preventDefault(); // keine Textauswahl beim Ziehen
        focusEl(frame);
      }
    });
    frame.addEventListener('pointermove', function (e) {
      if (!self.pointers.has(e.pointerId)) return;
      self.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      self._gestureMove();
    });
    var end = function (e) {
      if (!self.pointers.has(e.pointerId)) return;
      self.pointers.delete(e.pointerId);
      if (self.pointers.size) self._gestureStart(); // verbleibender Finger macht nahtlos weiter
      else self._gestureEnd();
    };
    frame.addEventListener('pointerup', end);
    frame.addEventListener('pointercancel', end);
    frame.addEventListener('lostpointercapture', end);
    // Safari: eigenes Seiten-Pinch auf dem Rahmen unterbinden (touch-action reicht auf alten iOS-Versionen nicht)
    frame.addEventListener('gesturestart', function (e) { e.preventDefault(); });
    frame.addEventListener('touchmove', function (e) { if (self.pointers.size) e.preventDefault(); }, { passive: false });
    // Trackpad-Pinch (Strg + Mausrad) zoomt, normales Mausrad scrollt weiter die Seite
    frame.addEventListener('wheel', function (e) {
      if (!e.ctrlKey || !self.photo) return;
      e.preventDefault();
      var rc = frame.getBoundingClientRect();
      var f = Math.exp(-e.deltaY * 0.01);
      self._setView(E.zoomView(self.view, f, (e.clientX - rc.left) / rc.width, (e.clientY - rc.top) / rc.height, self.W, self.H, self.aspect));
    }, { passive: false });

    // Tastatur (Fokus auf dem Rahmen): Pfeile verschieben, + und − zoomen, R dreht
    frame.addEventListener('keydown', function (e) {
      if (e.target !== frame || e.altKey || e.ctrlKey || e.metaKey) return;
      var map = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down', '+': 'in', '=': 'in', '-': 'out', '_': 'out', r: 'rotate', R: 'rotate' };
      var a = map[e.key];
      if (!a) return;
      e.preventDefault();
      self.act(a);
    });

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(function () { if (self.photo) { self._layout(); self._draw(); } });
      this._ro.observe(this.stage);
    } else {
      window.addEventListener('resize', function () { if (self.photo) { self._layout(); self._draw(); } });
    }
  };

  Editor.prototype._gestureStart = function () {
    var pts = [];
    this.pointers.forEach(function (p) { pts.push(p); });
    var rc = this.frame.getBoundingClientRect();
    this._g = { view: E.normView(this.view, this.W, this.H, this.aspect), rect: rc, pts: pts.slice(0, 2) };
    if (pts.length >= 2) {
      this._g.mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      this._g.dist = Math.max(1, Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y));
    }
    this.frame.classList.add('is-dragging');
  };

  Editor.prototype._gestureMove = function () {
    var g = this._g;
    if (!g || !g.rect.width) return;
    var pts = [];
    this.pointers.forEach(function (p) { pts.push(p); });
    var fw = g.rect.width;
    var v;
    if (pts.length >= 2 && g.mid) {
      var mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      var dist = Math.max(1, Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y));
      v = E.zoomView(g.view, dist / g.dist, (g.mid.x - g.rect.left) / fw, (g.mid.y - g.rect.top) / g.rect.height, this.W, this.H, this.aspect);
      v = E.panView(v, (mid.x - g.mid.x) / fw, (mid.y - g.mid.y) / fw, this.W, this.H, this.aspect);
    } else if (pts.length === 1) {
      v = E.panView(g.view, (pts[0].x - g.pts[0].x) / fw, (pts[0].y - g.pts[0].y) / fw, this.W, this.H, this.aspect);
    }
    if (v) this._setView(v, false, true);
  };

  Editor.prototype._gestureEnd = function () {
    this._g = null;
    this.frame.classList.remove('is-dragging');
    this._changed();
  };

  // Bedienschritt aus Werkzeugleiste oder Tastatur
  Editor.prototype.act = function (name) {
    if (!this.photo) return;
    var W = this.W;
    var H = this.H;
    var a = this.aspect;
    var s = E.PAN_STEP;
    var v = this.view;
    var rotated = false;
    switch (name) {
      case 'left': v = E.panView(v, -s, 0, W, H, a); break;
      case 'right': v = E.panView(v, s, 0, W, H, a); break;
      case 'up': v = E.panView(v, 0, -s, W, H, a); break;
      case 'down': v = E.panView(v, 0, s, W, H, a); break;
      case 'in': v = E.zoomView(v, E.ZOOM_STEP, 0.5, 0.5, W, H, a); break;
      case 'out': v = E.zoomView(v, 1 / E.ZOOM_STEP, 0.5, 0.5, W, H, a); break;
      case 'rotate': v = E.rotateView(v); rotated = true; break;
      case 'reset': rotated = this.view.rot !== 0; v = E.defaultView(); break;
      default: return;
    }
    this._setView(v, rotated);
    this._announce();
  };

  Editor.prototype._setView = function (v, rotated, live) {
    this.view = v;
    // Nach dem Drehen darf die Buybox das Format anpassen (ruft dann setFrame)
    if (rotated && this.o.onRotate) this.o.onRotate(this);
    this.view = E.normView(this.view, this.W, this.H, this.aspect);
    this._schedule();
    if (!live) this._changed();
    else this._updateLowres(this.crop()); // auch während der Geste aktuell (Eigenschaften erst am Ende)
  };

  // Größe/Format geändert: neues Seitenverhältnis, Ausschnitt bleibt so weit wie möglich erhalten
  Editor.prototype.setFrame = function (aspect, sizeValue) {
    var changed = Math.abs(aspect - this.aspect) > 1e-9;
    this.aspect = aspect;
    this.sizeValue = sizeValue;
    if (!this.photo) return;
    if (changed) {
      this.view = E.normView(this.view, this.W, this.H, this.aspect);
      this._layout();
      this._draw();
    }
    this._changed();
  };

  Editor.prototype.rotatedDims = function () {
    return E.rotatedDims(this.W, this.H, this.view.rot);
  };

  // Ganzzahliger Ausschnitt in Originalpixeln (für URL und Bestellung), null ohne Foto
  Editor.prototype.crop = function () {
    if (!this.photo || !this.W || !this.H) return null;
    return E.cropInt(E.cropRect(this.W, this.H, this.view, this.aspect));
  };

  Editor.prototype.cropUrl = function () {
    var c = this.crop();
    return c ? E.cropUrl(this.photo.url, c) : '';
  };

  Editor.prototype.cropData = function () {
    var c = this.crop();
    return c ? E.cropData(c, { publicId: this.photo.publicId, width: this.W, height: this.H }) : null;
  };

  /**
   * Neues Foto nach erfolgreichem Upload. photo: {url, width, height, publicId}; work: fertige Arbeitskopie (Canvas,
   * aufrecht wie bei Cloudinary) oder null. Ohne passende Arbeitskopie (HEIC, Browser liest das Format nicht) wird
   * einmal ein verkleinertes JPEG von Cloudinary geladen.
   */
  Editor.prototype.setPhoto = function (photo, work) {
    this.clear();
    var token = this.token;
    this.photo = photo;
    this.W = photo.width;
    this.H = photo.height;
    this.view = E.defaultView();
    this.root.hidden = false;
    if (work && work.width && work.height && E.matchOrientation(work.width, work.height, this.W, this.H) === 'same') {
      this.work = work;
    } else {
      freeCanvas(work);
      this._loadRemote(token);
    }
    this._layout();
    this._draw();
    this._changed();
  };

  Editor.prototype._loadRemote = function (token) {
    var self = this;
    this._state('loading');
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = function () {
      if (token !== self.token) return;
      var m = E.matchOrientation(img.naturalWidth, img.naturalHeight, self.W, self.H);
      if (!m) { self._state('failed'); return; }
      if (m === 'swapped') {
        // Cloudinary liefert gedreht aus: dann gilt diese Ausrichtung auch für a_/c_crop
        var t = self.W;
        self.W = self.H;
        self.H = t;
      }
      var c = document.createElement('canvas');
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      var ctx = c.getContext('2d');
      if (!ctx) { self._state('failed'); return; }
      ctx.drawImage(img, 0, 0);
      self.work = c;
      self._state('ready');
      self.view = E.normView(self.view, self.W, self.H, self.aspect);
      self._layout();
      self._draw();
      self._changed();
    };
    img.onerror = function () {
      if (token === self.token) self._state('failed');
    };
    img.src = E.workUrl(this.photo.url);
  };

  Editor.prototype._state = function (st) {
    if (this.loadingEl) this.loadingEl.hidden = st !== 'loading';
    if (this.failedEl) this.failedEl.hidden = st !== 'failed';
    this.root.setAttribute('data-state', st);
    this._updateButtons();
  };

  // Foto ändern / Fehler: alles zurück, Vorschau-Cache leer, laufende Rechnungen verwerfen
  Editor.prototype.clear = function () {
    this.token++;
    this.closeDialogs();
    if (this._w && Object.keys(this._pending).length) this._killWorker();
    this._jobs = {};
    this._queue = Promise.resolve();
    this.cache.clear();
    freeCanvas(this.rotWork);
    freeCanvas(this.work);
    this.work = null;
    this.rotWork = null;
    this.rotWorkRot = -1;
    this.photo = null;
    this.W = 0;
    this.H = 0;
    this.view = E.defaultView();
    this.pointers.clear();
    this._g = null;
    if (this.statusEl) this.statusEl.textContent = '';
    this._updateLowres(null);
    clearTimeout(this._lowT);
    clearTimeout(this._lowClearT);
    this._lvlSaid = 0;
    if (this.lowresLiveEl) this.lowresLiveEl.textContent = '';
    if (this.tools.hidden) {
      this.tools.hidden = false;
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
    }
    this._state('ready');
    var ctx = this.canvas.getContext('2d');
    if (ctx && this.canvas.width) ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.root.hidden = true;
  };

  Editor.prototype.closeDialogs = function () {
    if (this.loupe) this.loupe.close();
    if (this.preview) this.preview.close();
  };

  Editor.prototype.destroy = function () {
    this.clear();
    this._killWorker();
    if (this._ro) this._ro.disconnect();
  };

  // Gedrehte Arbeitskopie (exakte 90°-Schritte, pixelgenau), nur die aktuelle Drehung wird vorgehalten
  Editor.prototype._rotated = function () {
    var rot = E.normRot(this.view.rot);
    var w = this.work;
    if (!w || rot === 0) return w;
    if (this.rotWork && this.rotWorkRot === rot) return this.rotWork;
    var c = document.createElement('canvas');
    var odd = rot % 180 !== 0;
    c.width = odd ? w.height : w.width;
    c.height = odd ? w.width : w.height;
    var ctx = c.getContext('2d');
    if (rot === 90) ctx.setTransform(0, 1, -1, 0, w.height, 0);
    else if (rot === 180) ctx.setTransform(-1, 0, 0, -1, w.width, w.height);
    else ctx.setTransform(0, -1, 1, 0, 0, w.width);
    ctx.drawImage(w, 0, 0);
    freeCanvas(this.rotWork);
    this.rotWork = c;
    this.rotWorkRot = rot;
    return c;
  };

  // Rahmengröße: Seitenverhältnis exakt, höchstens --mnz-frame-max-h hoch, Canvas in Gerätepixeln (max. 2x)
  Editor.prototype._layout = function () {
    var frame = this.frame;
    frame.style.setProperty('--mnz-ar', String(this.aspect));
    var cs = getComputedStyle(this.stage);
    var avail = this.stage.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    if (!(avail > 0)) return;
    var maxH = parseFloat(cs.getPropertyValue('--mnz-frame-max-h')) || 440;
    var w = Math.min(avail, maxH * this.aspect);
    var h = w / this.aspect;
    frame.style.width = w.toFixed(2) + 'px';
    frame.style.height = h.toFixed(2) + 'px';
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var cw = Math.max(1, Math.round(w * dpr));
    var ch = Math.max(1, Math.round(h * dpr));
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
    }
  };

  Editor.prototype._schedule = function () {
    var self = this;
    if (this._raf) return;
    var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };
    this._raf = raf(function () {
      self._raf = 0;
      self._draw();
    });
  };

  Editor.prototype._draw = function () {
    var ctx = this.canvas.getContext('2d');
    if (!ctx || !this.canvas.width) return;
    if (!this.work || !this.photo) {
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }
    var r = E.cropRect(this.W, this.H, this.view, this.aspect);
    drawCrop(ctx, this._rotated(), r, this.canvas.width, this.canvas.height);
  };

  // Nach jeder Änderung: Buttons, Auflösungshinweis, Bestell-Eigenschaften (Buybox)
  Editor.prototype._changed = function () {
    this._updateButtons();
    this._updateLowres(this.crop());
    if (this.o.onChange) this.o.onChange(this);
  };

  /*
   * Auflösungshinweis: ein Element, zwei Stufen (data-level 1/2). Die Stufe folgt live Zoom, Drehung, Format und Größe.
   * Bei gleicher Stufe ändern sich nur die Zahlen (feste Mindestbreite, Ziffern gleich breit: kein Umbruchwechsel).
   * Screenreader: eigene Live-Region neben dem Kasten, beschrieben nur bei einem Stufenwechsel (gebündelt), nicht bei
   * jedem Zoom-Schritt. Bestellen bleibt in jeder Stufe möglich (kein Einfluss auf Button oder Eigenschaften).
   */
  Editor.prototype._updateLowres = function (c) {
    var el = this.lowresEl;
    if (!el) return;
    var strongText = this.s.lowresStrong || '';
    var strongMin = String(strongText).trim() ? +this.o.strongMin || 0 : 0;
    var lvl = this.photo ? E.lowResLevel(c, this.sizeValue, this.o.thresholds, strongMin) : 0;
    if (lvl !== this._lvl) {
      this._lvl = lvl;
      el.setAttribute('data-level', String(lvl));
      el.classList.toggle('mnz-editor__lowres--strong', lvl === 2);
      if (lvl === 2) this._buildStrongText(strongText);
      else if (lvl === 1 && this.lowresTextEl) {
        this.lowresTextEl.textContent = this._lowres1;
        this._nums = null;
      }
      el.hidden = !lvl;
      this._announceLowres();
    }
    if (lvl === 2 && c && this._nums) {
      var b = E.formatInt(c.w);
      var h = E.formatInt(c.h);
      for (var i = 0; i < this._nums.length; i++) {
        var n = this._nums[i];
        var val = n.getAttribute('data-mnz-num') === 'breite' ? b : h;
        if (n.textContent !== val) n.textContent = val;
      }
    }
  };

  // Text des Warnkastens aus der Vorlage: Textknoten + Zahlen-Spans (kein innerHTML, Text aus dem Setting bleibt Text)
  Editor.prototype._buildStrongText = function (template) {
    var t = this.lowresTextEl;
    if (!t) return;
    t.textContent = '';
    this._nums = [];
    var parts = E.splitTemplate(template);
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].key) {
        var s = document.createElement('span');
        s.className = 'mnz-editor__num';
        s.setAttribute('data-mnz-num', parts[i].key);
        t.appendChild(s);
        this._nums.push(s);
      } else {
        t.appendChild(document.createTextNode(parts[i].text));
      }
    }
  };

  // Ansage nur bei Stufenwechsel: gebündelt (schnelles Hin und Her = eine Ansage der Endstufe), Region danach wieder leer
  Editor.prototype._announceLowres = function () {
    var self = this;
    var live = this.lowresLiveEl;
    if (!live) return;
    clearTimeout(this._lowT);
    this._lowT = setTimeout(function () {
      var lvl = self._lvl;
      if (lvl === self._lvlSaid) return;
      self._lvlSaid = lvl;
      clearTimeout(self._lowClearT);
      if (!lvl) {
        live.textContent = '';
        return;
      }
      live.textContent = self.lowresTextEl ? self.lowresTextEl.textContent.replace(/\s+/g, ' ').trim() : '';
      // Nicht als veraltete Kopie im Lesemodus stehen lassen, der sichtbare Kasten bleibt die Quelle
      self._lowClearT = setTimeout(function () { live.textContent = ''; }, E.LOWRES_LIVE_KEEP);
    }, E.LOWRES_SAY_DELAY);
  };

  Editor.prototype._updateButtons = function () {
    var ready = !!this.photo;
    var L = ready ? E.limits(this.view, this.W, this.H, this.aspect) : {};
    var v = E.normView(this.view, this.W || 1, this.H || 1, this.aspect);
    var isDefault = v.rot === 0 && v.zoom === 1 && Math.abs(v.cx - 0.5) < 1e-9 && Math.abs(v.cy - 0.5) < 1e-9;
    var state = { left: L.left, right: L.right, up: L.up, down: L.down, 'in': L.zoomIn, out: L.zoomOut, rotate: ready, reset: ready && !isDefault };
    var btns = this.tools.querySelectorAll('[data-mnz-ed]');
    for (var i = 0; i < btns.length; i++) {
      var on = !!state[btns[i].getAttribute('data-mnz-ed')];
      var val = on ? 'false' : 'true';
      if (btns[i].getAttribute('aria-disabled') !== val) btns[i].setAttribute('aria-disabled', val);
    }
    var hasWork = !!this.work;
    if (this.loupeBtn) this.loupeBtn.disabled = !hasWork;
    if (this.previewBtn) this.previewBtn.disabled = !hasWork;
  };

  // Screenreader: Zoom und Drehung nach Tastatur-/Button-Bedienung (gebündelt)
  Editor.prototype._announce = function () {
    var self = this;
    clearTimeout(this._annT);
    this._annT = setTimeout(function () {
      if (!self.statusEl || !self.photo) return;
      var v = E.normView(self.view, self.W, self.H, self.aspect);
      self.statusEl.textContent = E.fill(self.s.status, { zoom: Math.round(v.zoom * 100), grad: v.rot });
    }, 350);
  };

  /* ---------- Lupe ---------- */
  Editor.prototype.openLoupe = function (opener) {
    if (!this.loupe || !this.work) return;
    var c = this.loupeCanvas;
    var src = this._rotated();
    var r = E.cropRect(this.W, this.H, this.view, this.aspect);
    // Volle Auflösung der Arbeitskopie im Ausschnitt (max. 2000 px), die Anzeige skaliert per CSS
    var w = Math.max(1, Math.round((r.w * src.width) / r.rw));
    var h = Math.max(1, Math.round((r.h * src.height) / r.rh));
    c.width = w;
    c.height = h;
    drawCrop(c.getContext('2d'), src, r, w, h);
    this.loupe.el.style.setProperty('--mnz-ar', String(w / h)); // Popup passt das Bild per CSS in den freien Platz ein
    this.loupe.open(opener);
  };

  /* ---------- Farbvorschau ---------- */
  Editor.prototype.openPreview = function (opener) {
    if (!this.preview || !this.work) return;
    var values = (this.o.colors ? this.o.colors() : []).filter(function (v) { return E.colorCount(v) >= 2; });
    if (!values.length) return;
    this._buildTabs(values);
    var cur = this.o.selectedColor ? this.o.selectedColor() : null;
    var start = values.indexOf(cur) !== -1 ? cur : values[0];
    this.pv.el.style.setProperty('--mnz-ar', String(this.aspect)); // am Popup: Bildbereich und Bild richten sich danach
    this._selectTab(start, false);
    this.preview.open(opener);
  };

  Editor.prototype._buildTabs = function (values) {
    var tabs = this.pv.tabs;
    var key = values.join('|');
    if (tabs.getAttribute('data-values') === key) return;
    tabs.innerHTML = '';
    var panelId = this.pv.panel.id;
    for (var i = 0; i < values.length; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'mnz-tab';
      b.id = panelId + '-tab-' + i;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', 'false');
      b.setAttribute('aria-controls', panelId);
      b.setAttribute('tabindex', '-1');
      b.setAttribute('data-value', values[i]);
      b.textContent = values[i];
      tabs.appendChild(b);
    }
    tabs.setAttribute('data-values', key);
  };

  Editor.prototype._selectTab = function (value, focus) {
    var pv = this.pv;
    var list = pv.tabs.querySelectorAll('[role="tab"]');
    var active = null;
    for (var i = 0; i < list.length; i++) {
      var on = list[i].getAttribute('data-value') === value;
      list[i].setAttribute('aria-selected', on ? 'true' : 'false');
      list[i].setAttribute('tabindex', on ? '0' : '-1');
      if (on) active = list[i];
    }
    if (!active) return;
    pv.panel.setAttribute('aria-labelledby', active.id);
    if (focus) focusEl(active);
    pv.value = value;
    var cur = this.o.selectedColor ? this.o.selectedColor() : null;
    pv.choose.textContent = E.fill(this.s.previewChoose, { farben: value });
    pv.choose.hidden = value === cur;
    pv.canvas.setAttribute('aria-label', E.fill(this.s.previewAlt, { farben: value }));
    this._showPreview(value);
  };

  Editor.prototype._previewKey = function (n) {
    var c = this.crop();
    return [this.token, c.rot, c.x, c.y, c.w, c.h, n].join('|');
  };

  Editor.prototype._showPreview = function (value) {
    var self = this;
    var pv = this.pv;
    var token = this.token;
    var n = E.colorCount(value);
    var key = this._previewKey(n);
    pv.failed.hidden = true;
    var hit = this.cache.get(key);
    if (hit) {
      this._paintPreview(hit);
      pv.busy.hidden = true;
      pv.el.setAttribute('data-state', 'ready');
      this._prefetch(value);
      return;
    }
    pv.busy.hidden = false;
    pv.el.setAttribute('data-state', 'busy');
    this._quantize(n, key).then(function (res) {
      if (pv.value !== value || !self.preview.isOpen() || self._previewKey(n) !== key) return;
      self._paintPreview(res);
      pv.busy.hidden = true;
      pv.el.setAttribute('data-state', 'ready');
      self._prefetch(value);
    }, function (err) {
      if (pv.value !== value || !self.preview.isOpen()) return;
      // Rechnung wurde verworfen (Worker beendet), das Foto ist aber dasselbe: neu anstoßen
      if (err === 'stale') {
        if (token === self.token) self._showPreview(value);
        return;
      }
      pv.busy.hidden = true;
      pv.failed.hidden = false;
      pv.el.setAttribute('data-state', 'failed');
      if (window.console) console.error('[MnZ Farbvorschau]', err);
    });
  };

  // Übrige Farbanzahlen im Hintergrund vorrechnen, damit der Tab-Wechsel sofort geht (nur mit Worker)
  Editor.prototype._prefetch = function (value) {
    if (!this._worker()) return;
    var self = this;
    var values = Array.prototype.map.call(this.pv.tabs.querySelectorAll('[role="tab"]'), function (t) { return t.getAttribute('data-value'); });
    values.forEach(function (v) {
      if (v === value) return;
      var n = E.colorCount(v);
      var key = self._previewKey(n);
      if (!self.cache.has(key)) self._quantize(n, key).catch(function () { /* egal */ });
    });
  };

  Editor.prototype._paintPreview = function (res) {
    var c = this.pv.canvas;
    c.width = res.width;
    c.height = res.height;
    var ctx = c.getContext('2d');
    ctx.putImageData(new ImageData(res.data, res.width, res.height), 0, 0);
    this.pv.el.setAttribute('data-colors', String(res.colors));
    this.pv.el.setAttribute('data-ms', String(Math.round(res.ms)));
    this.pv.el.setAttribute('data-engine', res.engine);
    this._paintColors(res);
  };

  // R6: Farben der aktuellen Vorschau als Farbfelder (hell nach dunkel sortiert), Titel mit der Anzahl
  Editor.prototype._paintColors = function (res) {
    var box = this.pv.colors;
    if (!box) return;
    var pal = res.palette;
    if (!pal || !pal.length || !this.s.previewColors) {
      box.hidden = true;
      return;
    }
    var key = pal.map(function (c) { return c.join(','); }).join(';');
    if (box.getAttribute('data-key') === key) { box.hidden = false; return; }
    var list = pal.slice().sort(function (a, b) {
      return (b[0] * 0.2126 + b[1] * 0.7152 + b[2] * 0.0722) - (a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722);
    });
    var ul = box.querySelector('[data-mnz-preview-swatches]');
    ul.innerHTML = '';
    for (var i = 0; i < list.length; i++) {
      var li = document.createElement('li');
      li.className = 'mnz-preview__swatch';
      var name = E.fill(this.s.previewColor || '[nr]', { nr: i + 1 });
      li.setAttribute('title', name);
      li.setAttribute('aria-label', name);
      li.style.backgroundColor = 'rgb(' + list[i][0] + ',' + list[i][1] + ',' + list[i][2] + ')';
      ul.appendChild(li);
    }
    box.querySelector('[data-mnz-preview-colors-title]').textContent = E.fill(this.s.previewColors, { anzahl: list.length });
    box.setAttribute('data-key', key);
    box.setAttribute('data-count', String(list.length));
    box.hidden = false;
  };

  // Eingabe: aktueller Ausschnitt (mit Drehung) in Arbeitsauflösung
  Editor.prototype._quantInput = function () {
    var r = E.cropRect(this.W, this.H, this.view, this.aspect);
    var w;
    var h;
    if (this.aspect >= 1) {
      w = E.PREVIEW_LONG;
      h = Math.max(1, Math.round(E.PREVIEW_LONG / this.aspect));
    } else {
      h = E.PREVIEW_LONG;
      w = Math.max(1, Math.round(E.PREVIEW_LONG * this.aspect));
    }
    var c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    drawCrop(ctx, this._rotated(), r, w, h);
    var img = ctx.getImageData(0, 0, w, h);
    freeCanvas(c);
    return img;
  };

  // Eine Rechnung je Ausschnitt und Farbanzahl: läuft sie schon (z. B. vorgerechnet), wird sie mitbenutzt
  Editor.prototype._quantize = function (n, key) {
    var self = this;
    if (!this._jobs) this._jobs = {};
    if (this._jobs[key]) return this._jobs[key];
    var p = this._quantizeNow(n, key);
    this._jobs[key] = p;
    var drop = function () { if (self._jobs[key] === p) delete self._jobs[key]; };
    p.then(drop, drop);
    return p;
  };

  Editor.prototype._quantizeNow = function (n, key) {
    var self = this;
    var token = this.token;
    var id = ++this._qid;
    var img = this._quantInput();
    var input = { id: id, data: img.data, width: img.width, height: img.height, n: n, seed: E.PREVIEW_SEED, mode: E.PREVIEW_MODE };
    return new Promise(function (resolve, reject) {
      var t0 = now();
      var finish = function (res, engine) {
        if (token !== self.token) { reject('stale'); return; }
        var entry = { data: res.data, width: res.width, height: res.height, colors: res.colors, palette: res.palette || null, ms: now() - t0, engine: engine };
        self.cache.set(key, entry);
        while (self.cache.size > E.CACHE_MAX) self.cache.delete(self.cache.keys().next().value);
        self.lastQuant = { n: n, ms: entry.ms, engine: engine };
        resolve(entry);
      };
      var worker = self._worker();
      if (worker) {
        self._pending[id] = {
          key: key,
          ok: function (res) { finish(res, 'worker'); },
          // Worker fällt aus (z. B. CSP): diese Rechnung im Haupt-Thread nachholen
          fallback: function () {
            if (token !== self.token) { reject('stale'); return; }
            input.data = self._quantInput().data;
            self._runMain(input, function (res) { finish(res, 'main'); }, reject, token);
          },
          fail: reject
        };
        try {
          worker.postMessage(input, [input.data.buffer]);
        } catch (err) {
          delete self._pending[id];
          self._workerBroken = true;
          self._killWorker();
          input.data = self._quantInput().data;
          self._runMain(input, function (res) { finish(res, 'main'); }, reject, token);
        }
      } else {
        self._runMain(input, function (res) { finish(res, 'main'); }, reject, token);
      }
    });
  };

  // Nächstes Häppchen ohne den 4-ms-Mindesttakt verschachtelter setTimeout-Aufrufe (MessageChannel), sonst setTimeout
  var yieldTask = (function () {
    if (typeof MessageChannel === 'undefined') return function (fn) { setTimeout(fn, 0); };
    var ch = new MessageChannel();
    var queue = [];
    ch.port1.onmessage = function () {
      var fn = queue.shift();
      if (fn) fn();
    };
    return function (fn) {
      queue.push(fn);
      ch.port2.postMessage(0);
    };
  })();

  // Ohne Worker: Haupt-Thread in Häppchen (~12 ms), damit die Seite bedienbar bleibt. Aufträge laufen nacheinander.
  Editor.prototype._runMain = function (input, done, fail, token) {
    var self = this;
    this._queue = this._queue.then(function () {
      return new Promise(function (resolve) {
        if (token !== self.token) { fail('stale'); resolve(); return; }
        var job;
        try { job = E.Quant.createJob(input); } catch (err) { fail(err); resolve(); return; }
        var tick = function () {
          if (token !== self.token) { fail('stale'); resolve(); return; }
          var t0 = now();
          var fin;
          try {
            fin = job.step(function () { return now() - t0 > 12; });
          } catch (err) {
            fail(err);
            resolve();
            return;
          }
          if (fin) {
            done(job.result);
            resolve();
          } else {
            yieldTask(tick);
          }
        };
        yieldTask(tick);
      });
    });
  };

  // Worker aus Inline-Code (Blob-URL): new Worker(asset_url) scheitert, weil die Assets auf cdn.shopify.com liegen
  Editor.prototype._worker = function () {
    if (!E.useWorker || this._workerBroken || typeof Worker === 'undefined' || typeof Blob === 'undefined' || !window.URL || !URL.createObjectURL) return null;
    if (this._w) return this._w;
    var self = this;
    try {
      var code = 'var Q=(' + E.quantFactory.toString() + ')();' +
        'self.onmessage=function(e){var d=e.data,r;try{r=Q.run(d);}catch(err){self.postMessage({id:d.id,ok:false,error:String(err&&err.message||err)});return;}' +
        'self.postMessage({id:d.id,ok:true,data:r.data,width:r.width,height:r.height,colors:r.colors,palette:r.palette},[r.data.buffer]);};';
      this._wUrl = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
      var w = new Worker(this._wUrl);
      w.onmessage = function (e) {
        var d = e.data || {};
        var p = self._pending[d.id];
        if (!p) return;
        delete self._pending[d.id];
        if (d.ok) p.ok(d);
        else p.fail(d.error);
      };
      w.onerror = function (e) {
        if (e && e.preventDefault) e.preventDefault();
        self._workerBroken = true;
        var pending = self._pending;
        self._pending = {};
        self._killWorker();
        for (var id in pending) pending[id].fallback();
      };
      this._w = w;
      return w;
    } catch (err) {
      this._workerBroken = true;
      this._killWorker();
      return null;
    }
  };

  // Worker beenden. Offene Rechnungen gelten als verworfen ('stale'), damit nichts für immer auf ein Ergebnis wartet.
  Editor.prototype._killWorker = function () {
    var pending = this._pending;
    this._pending = {};
    for (var id in pending) pending[id].fail('stale');
    if (this._w) {
      try { this._w.terminate(); } catch (err) { /* egal */ }
      this._w = null;
    }
    if (this._wUrl) {
      try { URL.revokeObjectURL(this._wUrl); } catch (err) { /* egal */ }
      this._wUrl = null;
    }
  };

  root.MnzEditor = Editor;
})(typeof window !== 'undefined' ? window : globalThis);
